import { Link } from "react-router-dom";
import { useState, useEffect } from "react";
import { useAuth, API } from "../App";
import { SEO } from "../components/SEO";
import axios from "axios";
import Navbar from "../components/Navbar";
import Footer from "../components/Footer";
import { Calendar, Users, Trophy, BookOpen, MessageSquare, Play, ArrowRight, Star, BarChart3, Target, ShoppingBag, Newspaper, Zap, Clock, History, Flame, Gamepad2, Tv, Music, ArrowLeftRight } from "lucide-react";
import { Button } from "../components/ui/button";
import MatchDayLive from "../components/MatchDayLive";
import TopPredictorsWidget from "../components/TopPredictorsWidget";

const BreakingNewsBanner = () => {
  const [latestNews, setLatestNews] = useState([]);
  const [isVisible, setIsVisible] = useState(true);
  const [retryCount, setRetryCount] = useState(0);

  const fetchLatestNews = async () => {
    try {
      const response = await axios.get(`${API}/news/latest?team=men`);
      if (response.data && response.data.length > 0) {
        const genericTitles = ['latest news', 'news', 'home', 'west ham', 'whufc'];
        const goodNews = response.data.filter(item => {
          const headline = item.headline.toLowerCase();
          return headline.length > 20 && !genericTitles.includes(headline.trim());
        }).slice(0, 10);
        setLatestNews(goodNews);
      } else {
        setLatestNews([]);
      }
    } catch (error) {
      console.error("Failed to fetch latest news:", error);
      setLatestNews([]);
    }
  };

  useEffect(() => {
    fetchLatestNews();
  }, []);

  // Auto-retry when empty (uses state counter to guarantee re-renders)
  useEffect(() => {
    if (latestNews.length === 0 && retryCount < 8) {
      const timer = setTimeout(() => {
        setRetryCount(prev => prev + 1);
        fetchLatestNews();
      }, 5000);
      return () => clearTimeout(timer);
    }
  }, [latestNews, retryCount]);

  if (latestNews.length === 0 || !isVisible) return null;

  return (
    <div className="bg-gradient-to-r from-[#F8D742] via-[#FFE066] to-[#F8D742] relative overflow-hidden" data-testid="breaking-news-banner">
      <div className="absolute inset-0 opacity-10" style={{backgroundImage: "repeating-linear-gradient(90deg, #000 0px, #000 1px, transparent 1px, transparent 20px), repeating-linear-gradient(0deg, #000 0px, #000 1px, transparent 1px, transparent 20px)"}}></div>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-2 relative">
        <div className="flex items-center gap-4">
          {/* Breaking Label */}
          <div className="flex items-center gap-2 flex-shrink-0 pr-4 border-r border-[#7C2C3B]/30">
            <Zap className="w-4 h-4 text-[#7C2C3B] animate-pulse" />
            <span className="text-[#7C2C3B] font-bold text-xs uppercase tracking-wider whitespace-nowrap" style={{ fontFamily: 'Barlow Condensed' }}>
              LATEST
            </span>
          </div>
          
          {/* Animated Ticker */}
          <div className="flex-1 overflow-hidden relative">
            <div className="animate-ticker flex items-center gap-8 whitespace-nowrap">
              {/* Duplicate the news items for seamless loop */}
              {[...latestNews, ...latestNews].map((news, idx) => (
                <Link 
                  key={idx}
                  to="/news"
                  className="inline-flex items-center gap-3 text-[#0F172A] font-medium text-sm hover:text-[#7C2C3B] transition-colors"
                  data-testid={`ticker-item-${idx}`}
                >
                  <span className="w-1.5 h-1.5 rounded-full bg-[#7C2C3B]"></span>
                  <span>{news.headline}</span>
                </Link>
              ))}
            </div>
          </div>
          
          {/* Close button */}
          <div className="flex items-center gap-3 flex-shrink-0 pl-4 border-l border-[#7C2C3B]/30">
            <Link 
              to="/news" 
              className="text-[#7C2C3B] text-xs font-medium hover:underline hidden sm:block"
            >
              More →
            </Link>
            <button 
              onClick={() => setIsVisible(false)}
              className="text-[#7C2C3B]/60 hover:text-[#7C2C3B] text-lg leading-none"
              aria-label="Dismiss"
            >
              ×
            </button>
          </div>
        </div>
      </div>
      
      {/* CSS for ticker animation */}
      <style>{`
        @keyframes ticker {
          0% { transform: translateX(0); }
          100% { transform: translateX(-50%); }
        }
        .animate-ticker {
          animation: ticker 30s linear infinite;
        }
        .animate-ticker:hover {
          animation-play-state: paused;
        }
      `}</style>
    </div>
  );
};

const LiveScoresBanner = () => {
  const [matches, setMatches] = useState(null);
  const [loading, setLoading] = useState(true);
  const [retryCount, setRetryCount] = useState(0);

  const fetchMatches = async () => {
    try {
      const response = await axios.get(`${API}/matches/today`);
      setMatches(response.data);
    } catch (error) {
      console.error("Failed to fetch matches:", error);
      setMatches(null);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchMatches();
  }, []);

  // Auto-retry when no matches (uses state counter to guarantee re-renders)
  useEffect(() => {
    const allMatches = [
      ...(matches?.mens?.matches || []),
      ...(matches?.womens?.matches || [])
    ];
    if (!loading && allMatches.length === 0 && retryCount < 8) {
      const timer = setTimeout(() => {
        setRetryCount(prev => prev + 1);
        fetchMatches();
      }, 5000);
      return () => clearTimeout(timer);
    }
  }, [loading, matches, retryCount]);

  if (loading) return null;

  const allMatches = [
    ...(matches?.mens?.matches || []).map(m => ({ ...m, league: m.competition || "Championship" })),
    ...(matches?.womens?.matches || []).map(m => ({ ...m, league: m.competition || "WSL" }))
  ];

  if (allMatches.length === 0) return null;

  return (
    <div className="bg-[#0F172A] text-white py-3" data-testid="live-scores-banner">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center gap-4 overflow-x-auto scrollbar-hide">
          <div className="flex items-center gap-2 flex-shrink-0 pr-4 border-r border-white/20">
            <Clock className="w-4 h-4 text-[#F8D742]" />
            <span className="text-xs font-bold uppercase tracking-wider" style={{ fontFamily: 'Barlow Condensed' }}>
              Latest Scores
            </span>
          </div>
          
          <div className="flex items-center gap-6 overflow-x-auto">
            {allMatches.slice(0, 6).map((match, idx) => (
              <Link 
                key={idx} 
                to="/fixtures"
                className="flex items-center gap-3 flex-shrink-0 hover:bg-white/10 px-3 py-1 rounded transition-colors"
              >
                <span className="text-[10px] text-white/60 uppercase">{match.league}</span>
                <div className="flex items-center gap-2">
                  <span className="text-sm font-medium" style={{ fontFamily: 'Barlow Condensed' }}>
                    {match.home_away === 'Home' ? 'WHU' : match.opponent?.substring(0, 3).toUpperCase() || 'OPP'}
                  </span>
                  <span className={`text-sm font-bold px-2 py-0.5 rounded ${match.is_completed ? 'bg-white/20' : 'bg-green-500 animate-pulse'}`}>
                    {match.score_home ?? '-'} - {match.score_away ?? '-'}
                  </span>
                  <span className="text-sm font-medium" style={{ fontFamily: 'Barlow Condensed' }}>
                    {match.home_away === 'Away' ? 'WHU' : match.opponent?.substring(0, 3).toUpperCase() || 'OPP'}
                  </span>
                </div>
                {match.is_completed && (
                  <span className="text-[10px] text-white/40">FT</span>
                )}
              </Link>
            ))}
          </div>
          
          <Link 
            to="/fixtures" 
            className="flex-shrink-0 text-xs text-[#2BAEE5] hover:text-white transition-colors ml-auto"
          >
            All Fixtures →
          </Link>
        </div>
      </div>
    </div>
  );
};

const OnThisDayBanner = () => {
  const [fact, setFact] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isExpanded, setIsExpanded] = useState(false);

  useEffect(() => {
    const fetchFact = async () => {
      try {
        const response = await axios.get(`${API}/on-this-day`);
        setFact(response.data);
      } catch (error) {
        console.error("Failed to fetch On This Day fact:", error);
      } finally {
        setLoading(false);
      }
    };
    fetchFact();
  }, []);

  if (loading || !fact) return null;

  // Get icon based on fact type
  const getTypeIcon = (type) => {
    switch(type) {
      case 'match': return '⚽';
      case 'signing': return '✍️';
      case 'birthday': return '🎂';
      case 'milestone': return '🏆';
      default: return '📅';
    }
  };

  const getTypeColor = (type) => {
    switch(type) {
      case 'match': return 'from-green-600 to-green-700';
      case 'signing': return 'from-blue-600 to-blue-700';
      case 'birthday': return 'from-pink-600 to-pink-700';
      case 'milestone': return 'from-amber-600 to-amber-700';
      default: return 'from-[#7C2C3B] to-[#63232F]';
    }
  };

  return (
    <div className={`bg-gradient-to-r ${getTypeColor(fact.type)} text-white relative overflow-hidden transition-all duration-300`} data-testid="on-this-day-banner">
      <div className="absolute inset-0 opacity-10" style={{backgroundImage: "url(\"data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23ffffff' fill-opacity='0.4'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E\")"}}></div>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-3 relative">
        <div className="flex items-center justify-between gap-4">
          <div className="flex items-center gap-3 min-w-0 flex-1">
            <div className="flex items-center gap-2 flex-shrink-0">
              <History className="w-4 h-4" />
              <span className="font-bold text-xs uppercase tracking-wider whitespace-nowrap" style={{ fontFamily: 'Barlow Condensed' }}>
                On This Day
              </span>
              <span className="text-white/80 text-xs hidden sm:inline">
                {fact.formatted_date}{fact.year ? `, ${fact.year}` : ''}
              </span>
            </div>
            <div className="h-4 w-px bg-white/30 hidden sm:block"></div>
            <div className="flex items-center gap-2 min-w-0 flex-1">
              <span className="text-lg flex-shrink-0">{getTypeIcon(fact.type)}</span>
              <div className="min-w-0 flex-1">
                <button 
                  onClick={() => setIsExpanded(!isExpanded)}
                  className="text-left w-full group"
                >
                  <span className="font-semibold text-sm block truncate group-hover:text-white/90 transition-colors">
                    {fact.title}
                  </span>
                  {!isExpanded && (
                    <span className="text-white/70 text-xs truncate block sm:hidden">
                      Tap to read more
                    </span>
                  )}
                </button>
              </div>
            </div>
          </div>
          <div className="flex items-center gap-2 flex-shrink-0">
            <button 
              onClick={() => setIsExpanded(!isExpanded)}
              className="text-white/80 hover:text-white text-xs font-medium hidden sm:block"
            >
              {isExpanded ? 'Less' : 'More'} →
            </button>
            <Link 
              to="/history"
              className="text-white/80 hover:text-white text-xs font-medium"
            >
              History
            </Link>
          </div>
        </div>
        
        {/* Expanded description */}
        <div className={`overflow-hidden transition-all duration-300 ${isExpanded ? 'max-h-40 opacity-100 mt-3' : 'max-h-0 opacity-0'}`}>
          <div className="bg-white/10 rounded-lg p-3 backdrop-blur-sm">
            <p className="text-sm text-white/90 leading-relaxed">
              {fact.description}
            </p>
            <div className="flex items-center gap-4 mt-2 pt-2 border-t border-white/20">
              <span className="text-xs text-white/60 capitalize">{fact.type}</span>
              {fact.year && <span className="text-xs text-white/60">{fact.year}</span>}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

const FeatureCard = ({ icon: Icon, title, description, link }) => (
  <Link to={link} className="group">
    <div className="bg-white rounded-xl p-6 shadow-sm border border-slate-200 hover:shadow-lg hover:border-[#2BAEE5] transition-all duration-300 h-full">
      <div className="w-12 h-12 rounded-lg bg-[#7C2C3B] flex items-center justify-center mb-4 group-hover:bg-[#2BAEE5] transition-colors">
        <Icon className="w-6 h-6 text-white" />
      </div>
      <h3 className="text-xl font-bold text-[#0F172A] mb-2" style={{ fontFamily: 'Barlow Condensed' }}>{title}</h3>
      <p className="text-slate-600 text-sm leading-relaxed">{description}</p>
      <div className="mt-4 flex items-center gap-2 text-[#7C2C3B] text-sm font-medium group-hover:text-[#2BAEE5] transition-colors">
        Explore <ArrowRight className="w-4 h-4" />
      </div>
    </div>
  </Link>
);

export default function LandingPage() {
  const { user } = useAuth();
  const [siteSettings, setSiteSettings] = useState({ tv_section_enabled: true });

  useEffect(() => {
    const fetchSiteSettings = async () => {
      try {
        const response = await axios.get(`${API}/site-settings`);
        setSiteSettings(response.data);
      } catch (error) {
        console.error("Failed to fetch site settings:", error);
      }
    };
    fetchSiteSettings();
  }, []);

  return (
    <div className="min-h-screen bg-slate-50" data-testid="landing-page">
      <SEO
        path="/"
        description="The ultimate West Ham United fan experience. Live scores, fixtures, player stats, ratings, chants, history, fan clubs and forums for Hammers supporters worldwide."
        jsonLd={{
          "@context": "https://schema.org",
          "@type": "WebSite",
          "name": "All WHUFC",
          "url": "https://allwhufc.com",
          "description": "The ultimate West Ham United fan experience",
          "potentialAction": {
            "@type": "SearchAction",
            "target": "https://allwhufc.com/players?q={search_term_string}",
            "query-input": "required name=search_term_string"
          }
        }}
      />
      <Navbar />
      <BreakingNewsBanner />
      {/* OnThisDayBanner removed as per user request */}
      <LiveScoresBanner />
      <MatchDayLive />
      
      {/* Hero Section */}
      <section className="relative bg-[#7C2C3B] hero-pattern overflow-hidden" data-testid="hero-section">
        <div className="absolute inset-0 bg-gradient-to-br from-[#7C2C3B] via-[#63232F] to-[#4A1A23] opacity-95"></div>
        
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20 lg:py-32">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div className="animate-fadeInUp">
              <div className="inline-flex items-center gap-2 bg-white/10 backdrop-blur-sm px-4 py-2 rounded-full mb-6">
                <Star className="w-4 h-4 text-[#F8D742]" />
                <span className="text-white/90 text-sm font-medium">The Ultimate Fan Community</span>
              </div>
              
              <h1 className="text-5xl md:text-7xl font-bold text-white mb-6 tracking-tight" style={{ fontFamily: 'Barlow Condensed' }}>
                ALL<br />
                <span className="text-[#2BAEE5]">WHUFC</span>
              </h1>
              
              <p className="text-xl text-white/80 mb-8 max-w-lg leading-relaxed">
                The ultimate West Ham United fan experience. Access exclusive content, fixtures, player profiles, league tables, and over 100 years of club history.
              </p>
              
              <div className="flex flex-wrap gap-4">
                {!user ? (
                  <>
                    <Link to="/register">
                      <Button className="bg-[#F8D742] text-[#0F172A] hover:bg-yellow-300 text-lg px-8 py-6 font-bold" data-testid="hero-subscribe-btn">
                        JOIN NOW
                      </Button>
                    </Link>
                    <Link to="/login">
                      <Button variant="outline" className="border-2 border-white text-white hover:bg-white hover:text-[#7C2C3B] text-lg px-8 py-6" data-testid="hero-login-btn">
                        LOGIN
                      </Button>
                    </Link>
                  </>
                ) : (
                  <Link to="/fixtures">
                    <Button className="bg-[#F8D742] text-[#0F172A] hover:bg-yellow-300 text-lg px-8 py-6 font-bold" data-testid="hero-explore-btn">
                      EXPLORE FIXTURES
                    </Button>
                  </Link>
                )}
              </div>

              {/* Stats */}
              <div className="grid grid-cols-3 gap-8 mt-12 pt-8 border-t border-white/20">
                <div>
                  <div className="text-3xl font-bold text-[#F8D742]" style={{ fontFamily: 'Barlow Condensed' }}>100+</div>
                  <div className="text-white/70 text-sm">Years of History</div>
                </div>
                <div>
                  <div className="text-3xl font-bold text-[#F8D742]" style={{ fontFamily: 'Barlow Condensed' }}>600+</div>
                  <div className="text-white/70 text-sm">Players Profiled</div>
                </div>
                <div>
                  <div className="text-3xl font-bold text-[#F8D742]" style={{ fontFamily: 'Barlow Condensed' }}>5</div>
                  <div className="text-white/70 text-sm">Team Squads</div>
                </div>
              </div>
            </div>

            {/* Hero Image */}
            <div className="relative hidden lg:block">
              <div className="absolute -inset-4 bg-[#2BAEE5]/20 rounded-3xl blur-3xl"></div>
              <img 
                src="https://images.unsplash.com/photo-1647782254748-98e9fc851075?auto=format&fit=crop&w=800&q=80" 
                alt="London Stadium - Home of West Ham United"
                className="relative rounded-2xl shadow-2xl"
              />
              <div className="absolute -bottom-6 -left-6 bg-white rounded-xl p-4 shadow-lg">
                <div className="flex items-center gap-3">
                  <div className="w-12 h-12 rounded-full bg-[#7C2C3B] flex items-center justify-center">
                    <Trophy className="w-6 h-6 text-[#F8D742]" />
                  </div>
                  <div>
                    <div className="font-bold text-[#0F172A]" style={{ fontFamily: 'Barlow Condensed' }}>LONDON STADIUM</div>
                    <div className="text-xs text-slate-500">Home since 2016</div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Wave Divider */}
        <div className="absolute bottom-0 left-0 right-0">
          <svg viewBox="0 0 1440 120" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M0 120L60 110C120 100 240 80 360 70C480 60 600 60 720 65C840 70 960 80 1080 85C1200 90 1320 90 1380 90L1440 90V120H1380C1320 120 1200 120 1080 120C960 120 840 120 720 120C600 120 480 120 360 120C240 120 120 120 60 120H0Z" fill="#f8fafc"/>
          </svg>
        </div>
      </section>

      {/* Features Section */}
      <section className="py-20 bg-slate-50" data-testid="features-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-[#0F172A] mb-4" style={{ fontFamily: 'Barlow Condensed' }}>
              EVERYTHING <span className="text-[#7C2C3B]">WEST HAM</span>
            </h2>
            <p className="text-slate-600 text-lg max-w-2xl mx-auto">
              Comprehensive coverage of all teams, competitions, and club history in one place
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            <FeatureCard 
              icon={Newspaper}
              title="NEWS & REVIEWS"
              description="Latest headlines, match reviews, and social updates from around the web. Auto-updated throughout the day."
              link="/news"
            />
            <FeatureCard 
              icon={Calendar}
              title="FIXTURES & RESULTS"
              description="Complete fixture list and scores for all teams - First Team, Women's, U16, U18, and U21s with goal scorers and match details."
              link="/fixtures"
            />
            <FeatureCard 
              icon={ArrowLeftRight}
              title="WEST HAM TRANSFERS"
              description="Latest transfer news plus the complete chronological transfer record — men's since 2000, women's since 2018. In, out, fees, dates and clubs."
              link="/transfers"
            />
            <FeatureCard 
              icon={BarChart3}
              title="LEAGUE TABLES"
              description="Live standings for the EFL Championship, Women's Super League, and academy teams. Track West Ham's position throughout the season."
              link="/tables"
            />
            <FeatureCard 
              icon={MessageSquare}
              title="FAN FORUM"
              description="Join the discussion! Connect with fellow Hammers fans and share your thoughts on all things West Ham."
              link="/forum"
            />
            <FeatureCard 
              icon={BookOpen}
              title="FAN MEMORIES"
              description="Relive the magic through supporters' stories. From Upton Park to Wembley, share and discover unforgettable moments."
              link="/fan-memories"
            />
            <FeatureCard 
              icon={Users}
              title="FAN CLUBS"
              description="Connect with Hammers supporters worldwide. Find your local fan club across 7 continents with 140+ global communities."
              link="/fan-clubs"
            />
            <FeatureCard 
              icon={ShoppingBag}
              title="ALL WHUFC SHOP"
              description="Browse West Ham United merch on Amazon. Shirts, accessories, gifts and more with easy click-through purchasing."
              link="/shop"
            />
            <FeatureCard 
              icon={Play}
              title="ENTERTAINMENT"
              description="Books, films, music, podcasts, and famous fans. Everything West Ham in popular culture."
              link="/entertainment"
            />
            <FeatureCard 
              icon={Target}
              title="TOP STATS"
              description="All-time top 100 goal scorers and appearance makers for both Men's and Women's First Teams."
              link="/stats"
            />
            <FeatureCard 
              icon={Users}
              title="PLAYER PROFILES"
              description="Every player who has worn the claret and blue. Complete profiles with stats, career summaries, and images."
              link="/players"
            />
            <FeatureCard 
              icon={BookOpen}
              title="CLUB HISTORY"
              description="In-depth 1000-word histories for every year since the club's inception. Journey through over a century of the Hammers."
              link="/history"
            />
            <FeatureCard 
              icon={Trophy}
              title="CUP COMPETITIONS"
              description="Detailed coverage of FA Cup, League Cup, and European campaigns with match reports and statistics."
              link="/cups"
            />
            <FeatureCard 
              icon={History}
              title="ON THIS DAY"
              description="366 historic West Ham moments - one for every day of the year. Relive goals, transfers, milestones and more."
              link="/on-this-day"
            />
            <FeatureCard 
              icon={Music}
              title="WEST HAM CHANTS"
              description="The full songbook of West Ham terrace chants. From Bubbles to Cockney Boys - every lyric, every song."
              link="/chants"
            />
          </div>

          {/* ALL WHUFC GAMES TV Tile - Conditional based on admin settings */}
          {siteSettings.tv_section_enabled && (
          <div className="mt-8">
            <Link to="/tv" data-testid="tv-tile">
              <div className="group relative bg-gradient-to-br from-[#0F172A] via-[#1e293b] to-[#334155] rounded-2xl p-8 overflow-hidden hover:scale-[1.02] transition-transform cursor-pointer border border-slate-700">
                {/* Animated background elements */}
                <div className="absolute inset-0 overflow-hidden">
                  <div className="absolute top-0 right-0 w-64 h-64 bg-[#7C2C3B] rounded-full -translate-y-1/2 translate-x-1/2 opacity-20 group-hover:opacity-30 transition-opacity"></div>
                  <div className="absolute bottom-0 left-0 w-48 h-48 bg-[#2BAEE5] rounded-full translate-y-1/2 -translate-x-1/2 opacity-20 group-hover:opacity-30 transition-opacity"></div>
                  {/* TV Signal lines */}
                  <div className="absolute top-4 right-4 flex items-center gap-2">
                    <div className="w-2 h-2 bg-red-500 rounded-full animate-pulse"></div>
                    <span className="text-white/60 text-xs uppercase tracking-wider">Live Schedule</span>
                  </div>
                </div>
                
                <div className="relative flex flex-col md:flex-row items-center gap-8">
                  {/* TV Icon */}
                  <div className="relative">
                    <div className="w-24 h-24 rounded-2xl bg-gradient-to-br from-white to-slate-200 flex items-center justify-center shadow-lg group-hover:scale-110 transition-transform">
                      <Tv className="w-12 h-12 text-[#0F172A]" />
                    </div>
                  </div>

                  {/* TV Info */}
                  <div className="flex-1 text-center md:text-left">
                    <div className="inline-flex items-center gap-2 bg-[#7C2C3B] text-white px-3 py-1 rounded-full text-xs font-bold uppercase tracking-wider mb-4">
                      <Tv className="w-3 h-3" />
                      TV Guide
                    </div>
                    <h3 className="text-3xl md:text-4xl font-bold text-white mb-3" style={{ fontFamily: 'Barlow Condensed' }}>
                      ALL WHUFC GAMES TV
                    </h3>
                    <p className="text-white/80 text-lg max-w-xl">
                      Find every West Ham match on TV worldwide. First Team, Women's, U21s and U18s - all broadcast schedules in one place.
                    </p>
                    
                    {/* Features */}
                    <div className="flex flex-wrap gap-4 mt-4">
                      <div className="flex items-center gap-2 text-white/60 text-sm">
                        <Calendar className="w-4 h-4 text-[#F8D742]" />
                        <span>14-Day Schedule</span>
                      </div>
                      <div className="flex items-center gap-2 text-white/60 text-sm">
                        <Users className="w-4 h-4 text-[#2BAEE5]" />
                        <span>All Teams</span>
                      </div>
                      <div className="flex items-center gap-2 text-white/60 text-sm">
                        <Play className="w-4 h-4 text-red-500" />
                        <span>YouTube Channels</span>
                      </div>
                    </div>
                  </div>

                  {/* Watch Button */}
                  <div className="flex-shrink-0">
                    <div className="w-16 h-16 rounded-full bg-[#7C2C3B] flex items-center justify-center group-hover:scale-110 transition-transform shadow-lg">
                      <ArrowRight className="w-8 h-8 text-white" />
                    </div>
                    <p className="text-white/60 text-xs uppercase tracking-wider mt-2 text-center">View</p>
                  </div>
                </div>
              </div>
            </Link>
          </div>
          )}

          {/* Match Streak Game Tile */}
          <div className="mt-8">
            <Link to="/match-streak" data-testid="match-streak-tile">
              <div className="group relative bg-gradient-to-br from-[#7C2C3B] via-[#5a1f2b] to-[#0F172A] rounded-2xl p-8 overflow-hidden hover:scale-[1.02] transition-transform cursor-pointer">
                {/* Animated background elements */}
                <div className="absolute inset-0 overflow-hidden">
                  <div className="absolute top-0 right-0 w-64 h-64 bg-[#2BAEE5] rounded-full -translate-y-1/2 translate-x-1/2 opacity-20 group-hover:opacity-30 transition-opacity"></div>
                  <div className="absolute bottom-0 left-0 w-48 h-48 bg-[#F8D742] rounded-full translate-y-1/2 -translate-x-1/2 opacity-20 group-hover:opacity-30 transition-opacity"></div>
                  <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-96 h-96 border-2 border-white/10 rounded-full animate-pulse"></div>
                </div>
                
                <div className="relative flex flex-col md:flex-row items-center gap-8">
                  {/* Game Icon */}
                  <div className="relative">
                    <div className="w-24 h-24 rounded-2xl bg-gradient-to-br from-[#F8D742] to-[#e6c23a] flex items-center justify-center shadow-lg group-hover:scale-110 transition-transform">
                      <Flame className="w-12 h-12 text-[#7C2C3B]" />
                    </div>
                    <div className="absolute -top-2 -right-2 w-8 h-8 rounded-full bg-[#2BAEE5] flex items-center justify-center animate-bounce">
                      <span className="text-white text-xs font-bold">NEW</span>
                    </div>
                  </div>

                  {/* Game Info */}
                  <div className="flex-1 text-center md:text-left">
                    <div className="inline-flex items-center gap-2 bg-[#F8D742] text-[#0F172A] px-3 py-1 rounded-full text-xs font-bold uppercase tracking-wider mb-4">
                      <Zap className="w-3 h-3" />
                      Featured Game
                    </div>
                    <h3 className="text-3xl md:text-4xl font-bold text-white mb-3" style={{ fontFamily: 'Barlow Condensed' }}>
                      MATCH STREAK
                    </h3>
                    <p className="text-white/80 text-lg max-w-xl">
                      Higher or Lower? Test your West Ham knowledge! Guess which player has more goals or appearances and build your streak.
                    </p>
                    
                    {/* Features */}
                    <div className="flex flex-wrap gap-4 mt-4">
                      <div className="flex items-center gap-2 text-white/60 text-sm">
                        <Target className="w-4 h-4 text-[#F8D742]" />
                        <span>Goals Game</span>
                      </div>
                      <div className="flex items-center gap-2 text-white/60 text-sm">
                        <Users className="w-4 h-4 text-[#2BAEE5]" />
                        <span>Appearances Game</span>
                      </div>
                      <div className="flex items-center gap-2 text-white/60 text-sm">
                        <Trophy className="w-4 h-4 text-[#F8D742]" />
                        <span>Leaderboards</span>
                      </div>
                    </div>
                  </div>

                  {/* Play Button */}
                  <div className="flex-shrink-0">
                    <div className="w-16 h-16 rounded-full bg-[#F8D742] flex items-center justify-center group-hover:scale-110 transition-transform shadow-lg">
                      <Play className="w-8 h-8 text-[#0F172A] ml-1" />
                    </div>
                    <p className="text-white/60 text-xs uppercase tracking-wider mt-2 text-center">Play Now</p>
                  </div>
                </div>
              </div>
            </Link>
          </div>

          {/* Score Predictor League Tile */}
          <div className="mt-8">
            <Link to="/score-predictor" data-testid="score-predictor-tile">
              <div className="group relative bg-gradient-to-br from-[#F8D742] via-[#e6c23a] to-[#7C2C3B] rounded-2xl p-8 overflow-hidden hover:scale-[1.02] transition-transform cursor-pointer">
                {/* Animated background elements */}
                <div className="absolute inset-0 overflow-hidden">
                  <div className="absolute top-0 right-0 w-64 h-64 bg-white rounded-full -translate-y-1/2 translate-x-1/2 opacity-10 group-hover:opacity-20 transition-opacity"></div>
                  <div className="absolute bottom-0 left-0 w-48 h-48 bg-[#7C2C3B] rounded-full translate-y-1/2 -translate-x-1/2 opacity-30 group-hover:opacity-40 transition-opacity"></div>
                </div>
                
                <div className="relative flex flex-col md:flex-row items-center gap-8">
                  {/* Trophy Icon */}
                  <div className="relative">
                    <div className="w-24 h-24 rounded-2xl bg-gradient-to-br from-[#7C2C3B] to-[#5a1f2b] flex items-center justify-center shadow-lg group-hover:scale-110 transition-transform">
                      <Trophy className="w-12 h-12 text-[#F8D742]" />
                    </div>
                    <div className="absolute -top-2 -right-2 w-8 h-8 rounded-full bg-[#2BAEE5] flex items-center justify-center animate-bounce">
                      <span className="text-white text-xs font-bold">NEW</span>
                    </div>
                  </div>

                  {/* Game Info */}
                  <div className="flex-1 text-center md:text-left">
                    <div className="inline-flex items-center gap-2 bg-[#7C2C3B] text-white px-3 py-1 rounded-full text-xs font-bold uppercase tracking-wider mb-4">
                      <Trophy className="w-3 h-3" />
                      Prediction League
                    </div>
                    <h3 className="text-3xl md:text-4xl font-bold text-[#0F172A] mb-3" style={{ fontFamily: 'Barlow Condensed' }}>
                      ALL WHUFC SCORE PREDICTION LEAGUE
                    </h3>
                    <p className="text-[#0F172A]/80 text-lg max-w-xl">
                      Predict the score of West Ham's next fixture. Earn points for correct predictions and climb the leaderboard!
                    </p>
                    <div className="flex items-center gap-4 mt-4">
                      <span className="text-sm text-[#0F172A]/70">Correct Score = <strong className="text-green-700">3 pts</strong></span>
                      <span className="text-sm text-[#0F172A]/70">Correct Result = <strong className="text-blue-700">1 pt</strong></span>
                    </div>
                  </div>

                  {/* Play Button */}
                  <div className="flex-shrink-0">
                    <div className="w-16 h-16 rounded-full bg-[#7C2C3B] flex items-center justify-center group-hover:scale-110 transition-transform shadow-lg">
                      <Target className="w-8 h-8 text-[#F8D742]" />
                    </div>
                    <p className="text-[#0F172A]/60 text-xs uppercase tracking-wider mt-2 text-center">Predict</p>
                  </div>
                </div>
              </div>
            </Link>
          </div>

          {/* Top Predictors — season-so-far podium, snapshotted Mondays 06:00 UK */}
          <TopPredictorsWidget />
        </div>
      </section>

      {/* Teams Section */}
      <section className="py-20 bg-white relative overflow-hidden" data-testid="teams-section">
        {/* Background Stadium Image */}
        <div className="absolute inset-0 opacity-5">
          <img 
            src="https://images.unsplash.com/photo-1632570458650-abf854df8124?auto=format&fit=crop&w=1920&q=80" 
            alt=""
            className="w-full h-full object-cover"
          />
        </div>
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-bold text-[#0F172A] mb-4" style={{ fontFamily: 'Barlow Condensed' }}>
              ALL TEAMS COVERED
            </h2>
            <p className="text-slate-600">Complete coverage from first team to academy</p>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
            {["Men's First Team", "Women's Team", "Under 21s", "Under 18s", "Under 16s"].map((team, index) => (
              <div key={team} className="bg-white/90 backdrop-blur-sm rounded-xl p-6 text-center border border-slate-200 hover:border-[#7C2C3B] transition-colors shadow-sm">
                <div className="w-16 h-16 rounded-full bg-[#7C2C3B] mx-auto mb-4 flex items-center justify-center">
                  <span className="text-white font-bold text-xl" style={{ fontFamily: 'Barlow Condensed' }}>
                    {index === 0 ? '1st' : index === 1 ? 'W' : `U${[21, 18, 16][index-2]}`}
                  </span>
                </div>
                <h3 className="font-bold text-[#0F172A] text-sm" style={{ fontFamily: 'Barlow Condensed' }}>{team.toUpperCase()}</h3>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-[#0F172A] relative overflow-hidden" data-testid="cta-section">
        {/* Background Stadium Image */}
        <div className="absolute inset-0 opacity-20">
          <img 
            src="https://images.unsplash.com/photo-1647782254748-98e9fc851075?auto=format&fit=crop&w=1920&q=80" 
            alt=""
            className="w-full h-full object-cover"
          />
        </div>
        <div className="relative max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <div className="inline-flex items-center gap-2 bg-[#7C2C3B] px-4 py-2 rounded-full mb-6">
            <span className="text-white text-sm font-medium">Join Today</span>
          </div>
          
          <h2 className="text-4xl md:text-5xl font-bold text-white mb-6" style={{ fontFamily: 'Barlow Condensed' }}>
            BECOME PART OF THE<br />
            <span className="text-[#2BAEE5]">WEST HAM FAMILY</span>
          </h2>
          
          <p className="text-slate-400 text-lg mb-8 max-w-2xl mx-auto">
            Get unlimited access to all content, join our exclusive community forum, and show your support for the Hammers.
          </p>

          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/register">
              <Button className="bg-[#F8D742] text-[#0F172A] hover:bg-yellow-300 text-lg px-8 py-6 font-bold" data-testid="cta-subscribe-btn">
                JOIN NOW
              </Button>
            </Link>
          </div>

          <p className="text-slate-500 text-sm mt-6">
            Cancel anytime. No long-term commitment required.
          </p>
        </div>
      </section>

      <Footer />
    </div>
  );
}
